import Tooltip from '@/components/Tooltip';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import ChannelLinksPopover from '@/components/ChannelLinksPopover';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { useAppSelector } from '@/store/hooks';
import { usePreviewMode } from '@/contexts/PreviewModeContext';

/* ──────────────────── 채널명 + 팝오버 ──────────────────── */

export function ChannelNameWithPopover({
  userId,
  name,
  thumbnailUrl,
  channelType,
  channelUrl,
  subtitle,
  showThumbnail = false,
  showChannelBadge = false,
  campaignHistoryCount,
  onChatSystemDrawer,
  onChannelDetailsDrawer,
  onStatsDrawer,
  onChannelEditDrawer,
}: {
  userId: string;
  name: string;
  thumbnailUrl?: string;
  channelType?: string;
  channelUrl?: string;
  subtitle?: string;
  /** Render the channel thumbnail avatar (with fallback) before the name — used by list
   *  pages that show the avatar inline (e.g. dedicated stats). Off by default. */
  showThumbnail?: boolean;
  /** Render the platform icon badge before the name. Off by default. */
  showChannelBadge?: boolean;
  /** 알고 있으면 팝오버의 「캠페인 이력」 항목 오른쪽에 개수를 바로 표시 (없으면 클릭 시 로드) */
  campaignHistoryCount?: number;
  /** 설정 시 팝오버 「채팅 시스템」은 인플루언서 캠페인(채팅) 화면을 드로어로 연다 */
  onChatSystemDrawer?: (userId: string) => void;
  /** 설정 시 팝오버 「채널 상세」는 채널 상세 패널을 드로어로 연다 */
  onChannelDetailsDrawer?: (userId: string) => void;
  /** 설정 시 팝오버 「통계 보기」는 stats 탭의 패널을 드로어로 연다 */
  onStatsDrawer?: (userId: string) => void;
  /** 설정 시 팝오버 「채널 수정」은 편집 다이얼로그를 연다 (Promise 반환 시 로딩 스피너 표시) */
  onChannelEditDrawer?: (userId: string) => void | Promise<void>;
}) {
  const socialLinks = channelUrl ? [{ type: channelType, url: channelUrl }] : undefined;
  // Internal-only actions (Chat System / Channel Details / Open stats / Edit) are
  // hidden for the advertiser preview and guest-token sessions — only a real admin
  // outside preview mode sees them (mirrors CampaignNavBox's isAdmin check).
  const isPreview = usePreviewMode();
  const role = useAppSelector((s) => s.envs.currentUser?.role);
  const hideInternalLinks = !(role === 'admin' && !isPreview);
  return (
    <ChannelLinksPopover
      userId={userId}
      channelName={name}
      channelType={channelType}
      subtitle={subtitle}
      thumbnailUrl={thumbnailUrl}
      socialLinks={socialLinks}
      className="channel-popover-wrap"
      hideDefaultLinks={hideInternalLinks}
      openTrigger="click"
      campaignHistoryCount={campaignHistoryCount}
      onChatSystemClick={onChatSystemDrawer}
      onChannelDetailsClick={onChannelDetailsDrawer}
      onStatsClick={onStatsDrawer}
      onChannelEditClick={onChannelEditDrawer}
    >
      {showThumbnail || showChannelBadge ? (
        <span className="inline-flex items-center gap-1.5">
          {showThumbnail && (
            <>
              <ChannelThumbnail src={thumbnailUrl} />
            </>
          )}
          {showChannelBadge && channelType && <ChannelTypeIcon type={channelType} size={14} asBadge />}
          <a
            href={`/panel/user/${userId}/overview`}
            target="_blank"
            rel="noopener noreferrer"
            className="influencer-name-link"
          >
            <Tooltip content={name} placement="top">
              <span className="influencer-name-text">{name}</span>
            </Tooltip>
          </a>
        </span>
      ) : (
        <a
          href={`/panel/user/${userId}/overview`}
          target="_blank"
          rel="noopener noreferrer"
          className="influencer-name-link"
        >
          <Tooltip content={name} placement="top">
            <span className="influencer-name-text">{name}</span>
          </Tooltip>
        </a>
      )}
    </ChannelLinksPopover>
  );
}
